import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ChevronRightIcon } from 'lucide-react';
export function Experience() {
  const experiences = [{
    company: 'Ditron Dental USA',
    role: 'Founder & Chairman of the Board',
    date: '2020 – Present',
    category: 'Board Leadership',
    location: 'Englewood, CO',
    description: 'Provide independent board leadership and strategic oversight for a growth-focused dental technology platform, driving long-term enterprise value.',
    achievements: ['Defines corporate strategy, governance frameworks, and long-term value creation priorities', 'Guides MedTech market expansion, innovation strategy, and competitive positioning', 'Oversees capital allocation, strategic partnerships, and regulatory compliance']
  }, {
    company: 'JET Investment Holdings, Inc.',
    role: 'Board Member',
    date: '2020 – Present',
    category: 'Investment Governance',
    location: 'United States',
    description: 'Serve as an active Board Member providing governance oversight across a diversified investment portfolio, focusing on sustainable growth.',
    achievements: ['Advises on investment strategy, capital deployment, and portfolio optimization', 'Provides oversight on risk governance and financial discipline frameworks', 'Contributes to M&A, growth initiatives, and market expansion strategy']
  }, {
    company: 'University of Utah School of Dentistry',
    role: 'Endowed Chair',
    date: '2018 – Present',
    category: 'Academic Leadership',
    location: 'Salt Lake City, UT',
    description: 'Lead a flagship academic and research program driving innovation in craniofacial science, regenerative medicine, and translational healthcare.',
    achievements: ['Leads a flagship research program in craniofacial science and regenerative medicine', 'Drives translational research, commercialization pathways, and IP development', 'Builds high-performing scientific teams and global academic partnerships']
  }, {
    company: 'University of Michigan Medical School',
    role: 'Visiting Professor',
    date: '2019 – Present',
    category: 'Academia',
    location: 'Ann Arbor, MI',
    description: 'Support global academic strategy and interdisciplinary innovation, delivering executive-level lectures in advanced surgical domains.',
    achievements: ['Delivers executive-level lectures in advanced surgical and clinical domains', 'Advises on research strategy and innovation pipelines', 'Mentors faculty and fosters cross-border academic partnerships']
  }, {
    company: 'ClearChoice Dental Implant Centers',
    role: 'Co-Founder',
    date: '2006 – 2018',
    category: 'Clinical Enterprise',
    location: 'National (USA)',
    description: 'Co-founded and scaled a national healthcare platform, driving operational excellence, market expansion, and enterprise growth.',
    achievements: ['Co-founded and scaled a national multi-site specialty healthcare platform', 'Established clinical governance, quality assurance, and operating frameworks', 'Executed strategic partnerships, capital raises, and growth initiatives']
  }, {
    company: 'Ole T. Jensen, DDS MS',
    role: 'Owner / Founder',
    date: '1980 – 2018',
    category: 'Clinical Practice',
    location: 'Denver, CO',
    description: 'Built and led a premier surgical practice, combining clinical excellence with strategic leadership and long-term value creation.',
    achievements: ['Founded and scaled a premier 38-year surgical practice', 'Established clinical governance and risk management protocols', 'Developed and mentored high-performing clinical and operational teams']
  }, {
    company: "St. Joseph's Hospital",
    role: 'Chairman',
    date: '1995 – 2006',
    category: 'Hospital Governance',
    location: 'Denver, CO',
    description: 'Provided executive leadership and governance oversight for a major clinical department, driving performance excellence and patient-centered care.',
    achievements: ['Led clinical governance and strategic departmental leadership', 'Drove multidisciplinary performance excellence and patient-centered care', 'Contributed to enterprise strategy and healthcare innovation initiatives']
  }, {
    company: "St. Luke's Hospital",
    role: 'Chairman, OMS Department',
    date: '1990 – 2010',
    category: 'Hospital Governance',
    location: 'Denver, CO',
    description: 'Led clinical governance, departmental strategy, and operational leadership within a major healthcare institution.',
    achievements: ['Led complex surgical programs and multidisciplinary care delivery models', 'Ensured regulatory compliance, patient safety, and risk mitigation', 'Mentored clinicians and strengthened leadership pipelines']
  }, {
    company: 'University of Colorado School of Dental Medicine',
    role: 'Adjunct Professor',
    date: '2000 – Present',
    category: 'Academia',
    location: 'Aurora, CO',
    description: 'Contribute to academic leadership and institutional excellence, shaping the next generation of healthcare innovators.',
    achievements: ['Provides academic governance input and curriculum innovation', 'Mentors high-potential professionals and supports succession pipelines', 'Elevates institutional standing through thought leadership and publications']
  }];
  return <section id="experience" className="py-24 md:py-32 bg-cream relative overflow-hidden" data-id="element-102">
      <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-103">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-20 text-center flex flex-col items-center" data-id="element-104">
          <div className="w-16 h-px bg-gold mb-6" data-id="element-105"></div>
          <h2 className="font-sans text-gold text-sm uppercase tracking-widest font-medium mb-4" data-id="element-106">
            Professional Experience
          </h2>
          <h3 className="font-serif text-4xl md:text-5xl text-navy font-medium leading-tight" data-id="element-107">
            A Distinguished Career
          </h3>
        </motion.div>

        {/* Timeline Container */}
        <div className="relative" data-id="element-108">
          {/* Center Line (Desktop) / Left Line (Mobile) */}
          <div className="absolute left-[28px] md:left-1/2 top-0 bottom-0 w-px bg-gold/30 md:-translate-x-1/2" data-id="element-109"></div>

          <div className="space-y-12 md:space-y-24" data-id="element-110">
            {experiences.map((exp, index) => {
            const isEven = index % 2 === 0;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.6
            }} className={`relative flex flex-col md:flex-row items-start ${isEven ? 'md:flex-row-reverse' : ''}`} data-id="element-111">
                  {/* Timeline Dot */}
                  <div className="absolute left-[28px] md:left-1/2 w-4 h-4 rounded-full bg-gold border-4 border-cream transform -translate-x-1/2 mt-6 md:mt-8 z-10 shadow-sm" data-id="element-112"></div>

                  {/* Content Card */}
                  <div className={`w-full pl-16 md:pl-0 md:w-1/2 ${isEven ? 'md:pl-16' : 'md:pr-16'}`} data-id="element-113">
                    <div className="relative bg-white p-8 md:p-10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group" data-id="element-114">
                      {/* Mini gold corner accents */}
                      <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-gold group-hover:border-gold-bright transition-colors" data-id="element-115"></div>
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-gold group-hover:border-gold-bright transition-colors" data-id="element-116"></div>

                      {/* Top row: category + date */}
                      <div className="flex items-center justify-between mb-4" data-id="element-117">
                        <span className="px-3 py-1 bg-navy/5 border border-gold/40 text-navy text-[10px] uppercase tracking-widest font-semibold rounded-sm" data-id="element-118">
                          {exp.category}
                        </span>
                        <span className="font-sans text-gold text-xs uppercase tracking-widest font-semibold" data-id="element-119">
                          {exp.date}
                        </span>
                      </div>

                      <h4 className="font-serif text-2xl text-navy font-medium mb-1" data-id="element-120">
                        {exp.role}
                      </h4>
                      <h5 className="font-sans text-charcoal/80 text-sm font-semibold uppercase tracking-wide mb-2" data-id="element-121">
                        {exp.company}
                      </h5>

                      {/* Location */}
                      <div className="flex items-center gap-1 mb-4" data-id="element-122">
                        <MapPinIcon size={12} className="text-muted" data-id="element-123" />
                        <span className="font-sans text-muted text-xs" data-id="element-124">
                          {exp.location}
                        </span>
                      </div>

                      <p className="font-sans text-charcoal/80 text-base leading-relaxed font-light mb-6" data-id="element-125">
                        {exp.description}
                      </p>

                      {/* Key Contributions */}
                      <h6 className="font-sans text-gold text-[10px] uppercase tracking-widest font-semibold mb-3" data-id="element-126">
                        Key Contributions
                      </h6>
                      <div className="space-y-2" data-id="element-127">
                        {exp.achievements.map((achievement, idx) => <motion.div key={idx} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      delay: 0.1 * idx,
                      duration: 0.4
                    }} className="flex items-start gap-2" data-id="element-128">
                            <ChevronRightIcon size={14} className="text-gold flex-shrink-0 mt-1" data-id="element-129" />
                            <p className="font-sans text-charcoal/80 text-sm leading-relaxed" data-id="element-130">
                              {achievement}
                            </p>
                          </motion.div>)}
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}